import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ISignInResponseDto } from '../modules/auth/Auth.model';
import { localStorageService } from './LocalStorage.service';
import { Constants } from '../util/Constants';

export async function isAuthenticated(): Promise<boolean> {
  const accessTokenValue = localStorageService.getEntry(
    Constants.TOKENS.ACCESS_TOKEN_STORAGE_KEY
  );
  if (!accessTokenValue) {
    return false;
  }

  // Require implementation
  //Check validity of the token or trigger refresh process

  return true;
}

export function getIdTokenEntry(): string {
  return getEntry(Constants.TOKENS.ID_TOKEN_STORAGE_KEY) as string;
}

export function getTokenTypeEntry(): string {
  return getEntry(Constants.TOKENS.TOKEN_TYPE_STORAGE_KEY) as string;
}

export function getExpiresInEntry(): string {
  return getEntry(Constants.TOKENS.EXPIRES_IN_STORAGE_KEY) as string;
}

export function getAccessTokenEntry(): string {
  return getEntry(Constants.TOKENS.ACCESS_TOKEN_STORAGE_KEY) as string;
}

export function getRefreshTokenEntry(): string {
  return getEntry(Constants.TOKENS.REFRESH_TOKEN_STORAGE_KEY) as string;
}

export function getIsTokenRefreshed(): string {
  return getEntry(Constants.TOKENS.IS_TOKEN_REFRESHED) as string;
}

export function setTokens(response: ISignInResponseDto): void {
  setIdTokenEntry(response?.tokens.idToken);
  setTokenTypeEntry(response.tokens.tokenType)
  setExpiresInEntry(response.tokens.expiresIn.toString())
  setAccessTokenEntry(response.tokens.accessToken)
  setRefreshTokenEntry(response.tokens.refreshToken)
}

export function getTokens(): ISignInResponseDto {
  return {
    tokens: {
      idToken: getIdTokenEntry() as string,
      tokenType: getTokenTypeEntry() as string,
      expiresIn: Number.parseInt(getExpiresInEntry()),
      accessToken: getAccessTokenEntry() as string,
      refreshToken: getRefreshTokenEntry() as string,
    },
  }
}

export function clearTokens(): void {
  removeEntry(Constants.TOKENS.ID_TOKEN_STORAGE_KEY);
  removeEntry(Constants.TOKENS.TOKEN_TYPE_STORAGE_KEY)
  removeEntry(Constants.TOKENS.EXPIRES_IN_STORAGE_KEY)
  removeEntry(Constants.TOKENS.ACCESS_TOKEN_STORAGE_KEY)
  removeEntry(Constants.TOKENS.REFRESH_TOKEN_STORAGE_KEY)
}

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const idToken = getIdTokenEntry()
  if (idToken) {
    config.headers.Authorization = `Bearer ${idToken}`
  }
  return config
}

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error)
}

const onResponse = (response: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  return response
}

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error)
}

export const tokenService = {
  isAuthenticated,
  getIdTokenEntry,
  getTokenTypeEntry,
  getExpiresInEntry,
  getAccessTokenEntry,
  getRefreshTokenEntry,
  setTokens,
  getTokens,
  clearTokens,
  setTokenRefreshed, 
  getIsTokenRefreshed, 
};

function setTokenRefreshed(isTokenRefreshed: string): void {
  setEntry(Constants.TOKENS.IS_TOKEN_REFRESHED, isTokenRefreshed);
}

function setIdTokenEntry(idToken: string): void {
  setEntry(Constants.TOKENS.ID_TOKEN_STORAGE_KEY, idToken);
}

function setTokenTypeEntry(tokenType: string): void {
  setEntry(Constants.TOKENS.TOKEN_TYPE_STORAGE_KEY, tokenType);
}

function setExpiresInEntry(expiresIn: string): void {
  setEntry(Constants.TOKENS.EXPIRES_IN_STORAGE_KEY, expiresIn);
}

function setAccessTokenEntry(accessToken: string): void {
  setEntry(Constants.TOKENS.ACCESS_TOKEN_STORAGE_KEY, accessToken);
}

function setRefreshTokenEntry(refreshToken: string): void {
  setEntry(Constants.TOKENS.REFRESH_TOKEN_STORAGE_KEY, refreshToken);
}

function setEntry(key: string, token: string): void {
  localStorageService.setEntry(key, token);
}

function getEntry(key: string): string | undefined {
  return localStorageService.getEntry(key);
}

function removeEntry(key: string): void {
  localStorageService.removeEntry(key)
}
